import api from "./api"; // centralized axios instance with baseURL + token

/**
 * 🔍 Fetch all pending approvals for the logged-in approver
 */
export const getPendingRequests = async () => {
  try {
    const res = await api.get("/approvals/pending/");
    return res.data;
  } catch (error) {
    console.error("Error fetching pending approvals:", error);
    throw error?.response?.data || error;
  }
};

/**
 * ✅ Approve a single request
 */
export const approveRequest = async (id, purchaseRequestUpdates = {}, comments = "") => {
  try {
    const payload = { status: "approved" };

    if (Object.keys(purchaseRequestUpdates).length) {
      payload.purchase_request = purchaseRequestUpdates;
    }

    if (comments) payload.comments = comments;

    const res = await api.patch(`/approvals/${id}/`, payload);
    return res.data;
  } catch (error) {
    console.error(`Error approving request ${id}:`, error);
    throw error?.response?.data || error;
  }
};

/**
 * ❌ Reject a single request
 */
export const rejectRequest = async (id, comments = "", purchaseRequestUpdates = {}) => {
  try {
    const payload = { status: "rejected" };

    if (comments) payload.comments = comments;
    if (Object.keys(purchaseRequestUpdates).length) {
      payload.purchase_request = purchaseRequestUpdates;
    }

    const res = await api.patch(`/approvals/${id}/`, payload);
    return res.data;
  } catch (error) {
    console.error(`Error rejecting request ${id}:`, error);
    throw error?.response?.data || error;
  }
};

/**
 * 📜 Fetch full approval history
 */
export const getApprovalsHistory = async () => {
  try {
    const res = await api.get("/approvals/");
    return res.data;
  } catch (error) {
    console.error("Error fetching approvals history:", error);
    throw error?.response?.data || error;
  }
};

/**
 * 🟩 Bulk Approve multiple items by IDs
 * ⚠️ Uses POST (backend requirement)
 */
export const bulkApproveRequests = async (ids, comments = "") => {
  if (!Array.isArray(ids) || ids.length === 0) {
    throw new Error("bulkApproveRequests requires an array of IDs");
  }

  try {
    const payload = {
      ids,
      status: "approved",
      ...(comments && { comments }),
    };

    const res = await api.post("/approvals/bulk/", payload);
    return res.data;
  } catch (error) {
    console.error("Error in bulkApproveRequests:", error);
    throw error?.response?.data || error;
  }
};

/**
 * 🟥 Bulk Reject multiple items by IDs
 * ⚠️ Uses POST (backend requirement)
 */
export const bulkRejectRequests = async (ids, comments = "") => {
  if (!Array.isArray(ids) || ids.length === 0) {
    throw new Error("bulkRejectRequests requires an array of IDs");
  }

  try {
    const payload = {
      ids,
      status: "rejected",
      ...(comments && { comments }),
    };

    const res = await api.post("/approvals/bulk/", payload);
    return res.data;
  } catch (error) {
    console.error("Error in bulkRejectRequests:", error);
    throw error?.response?.data || error;
  }
};

/**
 * 🔢 Bulk action by Lot Number
 * - IDs take priority over lotNumber
 * ⚠️ Uses POST
 */
export const bulkActionByLot = async (lotNumber = "", status, comments = "", ids = []) => {
  if (!lotNumber && (!Array.isArray(ids) || ids.length === 0)) {
    throw new Error("Provide a lot number or an array of IDs");
  }

  if (!["approved", "rejected"].includes(status)) {
    throw new Error("Status must be 'approved' or 'rejected'");
  }

  try {
    const payload = {
      status,
      ...(comments && { comments }),
      ...(ids.length > 0 ? { ids } : { lot_number: lotNumber }),
    };

    const res = await api.post("/approvals/bulk/", payload);
    return res.data;
  } catch (error) {
    console.error("Error in bulkActionByLot:", error);
    throw error?.response?.data || error;
  }
};

/**
 * 👤 Fetch current user
 */
export const getCurrentUser = async () => {
  try {
    const res = await api.get("/users/me/");
    return res.data;
  } catch (error) {
    console.error("Error fetching current user data:", error);
    throw error?.response?.data || error;
  }
};
